import { Tracker } from '@/utils/tracking';

import { STANDBY_CORE_EVENTS } from './standbyAnalyticsCore';
import type { StandbyCoreEventName } from './standbyAnalyticsCore';

// SQR-16001: typed vocabulary for standby analytics. Standby events carry a
// unified schema and a stable naming convention (standby_<object>_<verb>, actor
// out of the name). Every standby track() call goes through trackStandby so a
// renamed or typo'd event name fails compilation — the drift guard.
//
// The shared cross-app CORE (schema key/enum values, unified concept names,
// StandbyAnalyticsSchema) lives in ./standbyAnalyticsCore and is byte-identical
// with squire-mobile-commander. This file adds the SMF-specific funnel names and
// the emit helper.
//
// The app/platform/surface/app_version/user_type schema keys are injected by the
// RudderStack tracker (getTrackingPropertiesForEvent) for every standby_ event,
// so call sites never pass them; they own entry_id (entry-scoped events) plus any
// event-specific business props.

export type {
  StandbyAnalyticsSchema,
  StandbyApp,
  StandbyCoreEventName,
  StandbyPlatform,
  StandbySurface,
  StandbyUserType,
} from './standbyAnalyticsCore';

/** SMF standby event names. Unified-concept names reuse the shared core. */
export const STANDBY_EVENTS = {
  landingViewed: 'standby_landing_viewed',
  registrationFailed: 'standby_registration_failed',
  phoneSubmitted: 'standby_phone_submitted',
  otpSubmitted: 'standby_otp_submitted',
  otpSent: 'standby_otp_sent',
  otpVerificationFailed: 'standby_otp_verification_failed',
  otpSendFailed: 'standby_otp_send_failed',
  customerIdentified: 'standby_customer_identified',
  otpResendRequested: 'standby_otp_resend_requested',
  detailsSubmitted: 'standby_details_submitted',
  stylistListViewed: 'standby_stylist_list_viewed',
  stylistSelected: 'standby_stylist_selected',
  serviceSearchUsed: 'standby_service_search_used',
  serviceToggled: 'standby_service_toggled',
  servicesConfirmed: 'standby_services_confirmed',
  summaryViewed: 'standby_summary_viewed',
  entryCreateSubmitted: STANDBY_CORE_EVENTS.ENTRY_CREATE_SUBMITTED,
  entryCreated: STANDBY_CORE_EVENTS.ENTRY_CREATED,
  entryCreateFailed: STANDBY_CORE_EVENTS.ENTRY_CREATE_FAILED,
  statusViewed: 'standby_status_viewed',
  confirmationViewed: 'standby_confirmation_viewed',
  leaveStarted: 'standby_leave_started',
  leaveDismissed: 'standby_leave_dismissed',
  entryLeft: STANDBY_CORE_EVENTS.ENTRY_LEFT,
  offerViewed: 'standby_offer_viewed',
  offerExpiredViewed: 'standby_offer_expired_viewed',
  offerDeclined: 'standby_offer_declined',
  rebookStarted: 'standby_rebook_started',
  rejoinStarted: 'standby_rejoin_started',
  checkoutViewed: 'standby_checkout_viewed',
  tipSelected: 'standby_tip_selected',
  paymentSubmitted: 'standby_payment_submitted',
  paymentFailed: 'standby_payment_failed',
  bookingConfirmed: 'standby_booking_confirmed',
  drawerViewed: 'standby_drawer_viewed',
  drawerLeaveStarted: 'standby_drawer_leave_started',
  drawerLeaveDismissed: 'standby_drawer_leave_dismissed',
  homeCardViewed: 'standby_home_card_viewed',
  homeCardTapped: 'standby_home_card_tapped',
} as const;

/** Every standby event name SMF may emit, including the shared-core names. */
export type StandbyEventName =
  | StandbyCoreEventName
  | (typeof STANDBY_EVENTS)[keyof typeof STANDBY_EVENTS];

/** Which surface an entry event (left, declined, checkout viewed) came from. */
export type StandbyEntrySource = 'status' | 'drawer';

/**
 * Business props a call site may attach. `user_type` and the four platform keys
 * are tracker-injected and intentionally absent here.
 */
export type StandbyEventProps = {
  [key: string]: string | number | boolean | null | undefined;
  // Reserved: these are tracker-injected. Typing them `never` stops a call site
  // from overriding the injected schema metadata with caller-controlled values.
  app?: never;
  platform?: never;
  surface?: never;
  app_version?: never;
  user_type?: never;
};

/** Props for entry-scoped events, which must carry the correlation id. */
export interface StandbyEntryScopedProps extends StandbyEventProps {
  entry_id: string;
}

/**
 * Events tied to one specific entry — they must carry `entry_id`. Excludes the
 * pre-entry funnel (nothing to correlate yet), the two create-attempt events
 * (fire before the entry exists), and the aggregate home-card events (they
 * summarize every entry, so a single id would mislead).
 */
export type StandbyEntryScopedEventName =
  | typeof STANDBY_CORE_EVENTS.ENTRY_CREATED
  | typeof STANDBY_CORE_EVENTS.ENTRY_LEFT
  | typeof STANDBY_CORE_EVENTS.ENTRY_REMOVED
  | (typeof STANDBY_EVENTS)[
      | 'statusViewed'
      | 'confirmationViewed'
      | 'leaveStarted'
      | 'leaveDismissed'
      | 'offerViewed'
      | 'offerExpiredViewed'
      | 'offerDeclined'
      | 'rebookStarted'
      | 'rejoinStarted'
      | 'checkoutViewed'
      | 'tipSelected'
      | 'paymentSubmitted'
      | 'paymentFailed'
      | 'bookingConfirmed'
      | 'drawerViewed'
      | 'drawerLeaveStarted'
      | 'drawerLeaveDismissed'];

/**
 * The only way to emit a standby event. Gates the event NAME to
 * `StandbyEventName` (a renamed/typo'd name fails compilation), and via the
 * overloads REQUIRES `entry_id` for entry-scoped events while leaving props
 * optional for the rest.
 */
export function trackStandby(
  event: StandbyEntryScopedEventName,
  props: StandbyEntryScopedProps,
): void;
export function trackStandby(
  event: Exclude<StandbyEventName, StandbyEntryScopedEventName>,
  props?: StandbyEventProps,
): void;
export function trackStandby(
  event: StandbyEventName,
  props?: StandbyEventProps,
): void {
  if (props) {
    Tracker.track(event, props);
  } else {
    Tracker.track(event);
  }
}
